// The seasonal looks of the synchronization loader (EcosShelfProgress.vue, #23635). A theme replaces one or two
// products of the shelf (same slots, see SHELF in ecosShelf.js) and adds a light decor above the shelf, drawn behind
// the products. Everything ships with the app: nothing to download, works offline. Which theme shows when is
// decided by src/utils/domain/seasonalTheme.js.
// Design rules (validated with marketing): no alcohol, no religious, sexual or patriarchal symbol; sport events stay
// generic, without logo, official name or protected emblem (rings, flame, competition or race name, yellow jersey).
// A decor item is { d, fill?, stroke?, sw?, anim?, i? }: `anim` is its `.ecos-shelf-progress__decor--<anim>`
// animation (src/styles/_ecosShelfProgress.scss), `i` staggers it.
import { ell, rr } from '@/utils/ui/ecosShelf';

/** Room added above the shelf for the decor. */
export const THEME_VIEWBOX = '0 -36 760 166';

const MULTI = ['#E04A3C', '#F4C44E', '#6F8FD6', '#ABE49A', '#EE9C94'];

/** Heart 98 × s wide, tip down, top of the lobes at `top`, centred on cx. */
const heart = (cx, top, s = 1) => {
	const P = (x, y) => `${(cx + (x - 61) * s).toFixed(1)} ${(top + (y - 28) * s).toFixed(1)}`;
	return `M${P(61, 120)}C${P(40, 104)} ${P(12, 84)} ${P(12, 58)}C${P(12, 40)} ${P(26, 28)} ${P(40, 28)}` + `C${P(50, 28)} ${P(57, 34)} ${P(61, 42)}C${P(65, 34)} ${P(72, 28)} ${P(82, 28)}` + `C${P(96, 28)} ${P(110, 40)} ${P(110, 58)}C${P(110, 84)} ${P(82, 104)} ${P(61, 120)}Z`;
};

/* ======================= Seasonal products ======================= */

// Epiphany: galette and its paper crown
const galette = () => [
	['M10 96V110Q10 120 30 120H92Q112 120 112 110V96Z', '#D99A3C'],
	[ell(61, 96, 51, 14), '#E9B44C'],
	['M40 90Q61 85 82 90Q61 87 40 90Z', '#C98B2E'],
	['M24 97Q61 89 98 97Q61 92 24 97Z', '#C98B2E'],
	['M34 103Q61 97 88 103Q61 100 34 103Z', '#C98B2E'],
	['M38 88V58L48 70L61 52L74 70L84 58V88Z', '#F4C44E'],
	[rr(38, 79, 46, 9), '#E0A82E'],
	[ell(47, 83.5, 2.5), '#6F8FD6'],
	[ell(61, 83.5, 3), '#E04A3C'],
	[ell(75, 83.5, 2.5), '#6F8FD6'],
];

// Valentine's day: heart-shaped box of chocolates
const heartBox = () => [
	[heart(61, 28), '#E04A3C'],
	[heart(61, 40, 0.74), '#EE9C94'],
	[rr(57, 42, 8, 70), '#F4C44E'],
	['M61 42C52 28 38 30 46 42Z', '#F4C44E'],
	['M61 42C70 28 84 30 76 42Z', '#F4C44E'],
];

// Carnival: mask sold on a card
const carnivalMask = () => [
	[rr(46, 8, 30, 16, 4), '#B0C2E8'],
	[rr(14, 18, 94, 102, 6), '#B0C2E8'],
	[ell(61, 15, 6, 3.5), '#FFFFFF'],
	['M30 52C18 32 24 18 33 22C37 32 37 42 35 52Z', '#F4C44E'],
	['M38 50C32 32 38 22 45 26C46 34 45 42 42 50Z', '#ABE49A'],
	['M24 64C24 48 44 44 61 54C78 44 98 48 98 64C98 82 80 86 61 74C42 86 24 82 24 64Z', '#E04A3C'],
	[ell(44, 64, 9, 6), '#B0C2E8'],
	[ell(78, 64, 9, 6), '#B0C2E8'],
	[rr(28, 98, 66, 12, 3), '#DCE5F7'],
];

// Easter: chocolate bunny and decorated egg
const chocolateBunny = () => [
	['M44 42C34 14 38 -6 47 -2C55 2 56 24 53 42Z', '#6B3F27'],
	['M66 42C64 22 68 0 77 0C86 2 82 24 72 44Z', '#6B3F27'],
	['M46 34C41 18 42 6 47 6C51 8 51 22 50 34Z', '#8A5634'],
	['M69 34C68 20 71 8 75 8C79 10 77 22 72 36Z', '#8A5634'],
	[ell(60, 90, 36, 30), '#6B3F27'],
	[ell(59, 52, 19, 18), '#6B3F27'],
	[ell(62, 96, 19, 18), '#8A5634'],
	[rr(40, 66, 40, 6, 3), '#EE9C94'],
	['M60 69C50 58 40 64 46 72Z', '#EE9C94'],
	['M60 69C70 58 80 64 74 72Z', '#EE9C94'],
];
const decoratedEgg = () => [
	['M174 20C204 20 212 78 212 92C212 110 196 120 174 120C152 120 136 110 136 92C136 78 144 20 174 20Z', '#FFE4A2'],
	['M140.4 62H207.6L210.1 74H137.9Z', '#EE9C94'],
	[ell(156, 85, 3.5), '#6F8FD6'],
	[ell(174, 85, 3.5), '#6F8FD6'],
	[ell(192, 85, 3.5), '#6F8FD6'],
	['M136.3 96H211.7L210.1 102H137.9Z', '#B0C2E8'],
];

// Back to school: box of coloured pencils and spiral notebook
const pencilBox = () => {
	const colors = ['#E04A3C', '#F4C44E', '#4CB050', '#6F8FD6', '#EE9C94', '#F05E00'];
	const offsets = [0, 6, 2, 8, 4, 1];
	const shapes = [];
	colors.forEach((c, k) => {
		const x = 22 + k * 14;
		const y = 34 + offsets[k];
		shapes.push([`M${x} ${y}L${x + 5} ${y - 12}L${x + 10} ${y}Z`, '#F2D2A9']);
		shapes.push([`M${x + 3.3} ${y - 8}L${x + 5} ${y - 12}L${x + 6.7} ${y - 8}Z`, c]);
		shapes.push([rr(x, y, 10, 40), c]);
	});
	shapes.push([rr(14, 60, 94, 60, 4), '#3F63B5']);
	shapes.push([rr(14, 60, 94, 8, 4, 4, 0, 0), '#96A9D6']);
	shapes.push([rr(30, 84, 62, 18, 3), '#DCE5F7']);
	return shapes;
};
const notebook = () => {
	const shapes = [
		[rr(130, 22, 88, 98, 3), '#6F8FD6'],
		[rr(150, 42, 56, 26, 2), '#DCE5F7'],
		[rr(156, 50, 44, 2), '#B0C2E8'],
		[rr(156, 57, 44, 2), '#B0C2E8'],
	];
	for (let y = 30; y <= 112; y += 10) shapes.push([ell(136, y, 3), '#DCE5F7']);
	return shapes;
};

// Halloween: pumpkin candy bucket
const pumpkinBucket = () => [
	['M24 76C24 30 98 30 98 76L93 76C93 38 29 38 29 76Z', '#585858'],
	[ell(46, 66, 9, 6), '#EE9C94'],
	[ell(64, 63, 9, 6), '#ABE49A'],
	[ell(80, 67, 8, 5), '#F4C44E'],
	[ell(38, 96, 24, 24), '#D95500'],
	[ell(84, 96, 24, 24), '#D95500'],
	[ell(61, 94, 26, 26), '#F05E00'],
	['M44 88L52 88L48 80Z', '#3D2A1C'],
	['M70 88L78 88L74 80Z', '#3D2A1C'],
	['M44 100Q61 112 78 100L72 104L66 101L61 106L56 101L50 104Z', '#3D2A1C'],
];

// Saint Nicholas: boot filled with treats
const treatBoot = () => [
	[ell(40, 40, 10), '#F05E00'],
	['M38 31C40 24 46 24 48 28C45 30 41 31 38 31Z', '#4CB050'],
	[ell(61, 34, 9), '#F4C44E'],
	[ell(61, 34, 6), '#E0A82E'],
	['M72 46L80 22L92 26L84 50Z', '#6B3F27'],
	['M26 50H76V92C90 92 104 98 104 110V120H26Z', '#E04A3C'],
	[rr(22, 42, 58, 14, 6), '#DCE5F7'],
	[rr(24, 114, 82, 6, 0, 0, 3, 3), '#B8322A'],
];

// Christmas: gift box
const giftBox = () => [
	[rr(14, 44, 94, 76, 4), '#E04A3C'],
	[rr(14, 74, 94, 10), '#F4C44E'],
	[rr(10, 34, 102, 14, 3), '#B8322A'],
	[rr(55, 34, 12, 86), '#F4C44E'],
	['M61 34C48 14 30 20 42 34Z', '#F4C44E'],
	['M61 34C74 14 92 20 80 34Z', '#F4C44E'],
	[rr(56, 27, 10, 9, 3), '#E0A82E'],
];

// New Year's Eve: party hat
const partyHat = () => [
	['M61 22L100 116Q61 122 22 116Z', '#6F8FD6'],
	['M49.6 50H72.4L76.4 60H45.6Z', '#F4C44E'],
	['M37.4 80H84.6L88.6 90H33.4Z', '#F4C44E'],
	[ell(61, 40, 3), '#EE9C94'],
	[ell(55, 70, 3), '#EE9C94'],
	[ell(68, 70, 3), '#EE9C94'],
	[ell(48, 102, 3), '#EE9C94'],
	[ell(74, 102, 3), '#EE9C94'],
	[ell(61, 18, 9), '#F4C44E'],
];

/* ======================= Sport events ======================= */

const poly = (pts) => 'M' + pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join('L') + 'Z';
const pentagonPoints = (cx, cy, r, rotDeg) =>
	Array.from({ length: 5 }, (_, k) => {
		const a = ((rotDeg + k * 72) * Math.PI) / 180;
		return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
	});
// Rough clipping of a polygon by a disk: what sticks out is brought back onto the circle
const clipDisk = (pts, cx, cy, R) => {
	const out = [];
	pts.forEach((p, i) => {
		const q = pts[(i + 1) % pts.length];
		for (let s = 0; s < 24; s++) {
			const t = s / 24;
			let x = p[0] + (q[0] - p[0]) * t;
			let y = p[1] + (q[1] - p[1]) * t;
			const d = Math.hypot(x - cx, y - cy);
			if (d > R) {
				x = cx + ((x - cx) * R) / d;
				y = cy + ((y - cy) * R) / d;
			}
			out.push([x, y]);
		}
	});
	return out;
};
const seam = ([x1, y1], [x2, y2], w = 1.6) => {
	const dx = x2 - x1;
	const dy = y2 - y1;
	const L = Math.hypot(dx, dy);
	const nx = (-dy / L) * (w / 2);
	const ny = (dx / L) * (w / 2);
	return poly([
		[x1 + nx, y1 + ny],
		[x2 + nx, y2 + ny],
		[x2 - nx, y2 - ny],
		[x1 - nx, y1 - ny],
	]);
};

// Football: generic ball (pentagons and seams), no logo nor official name
const football = () => {
	const cx = 61;
	const cy = 76;
	const R = 43;
	const DARK = '#2B2F3A';
	const LINE = '#7A808C';
	const shapes = [
		[ell(cx, cy, R + 1.5), '#585858'],
		[ell(cx, cy, R), '#FFFFFF'],
	];
	const inner = pentagonPoints(cx, cy, 12, -90);
	const outers = Array.from({ length: 5 }, (_, k) => {
		const a = -90 + k * 72;
		const ar = (a * Math.PI) / 180;
		return pentagonPoints(cx + 39 * Math.cos(ar), cy + 39 * Math.sin(ar), 12, a + 180);
	});
	const clamp = ([x, y]) => {
		const d = Math.hypot(x - cx, y - cy);
		return d > R - 1 ? [cx + ((x - cx) * (R - 1)) / d, cy + ((y - cy) * (R - 1)) / d] : [x, y];
	};
	outers.forEach((o, k) => {
		shapes.push([seam(inner[k], o[0]), LINE]);
		shapes.push([seam(clamp(o[4]), clamp(outers[(k + 1) % 5][1])), LINE]);
	});
	shapes.push([poly(inner), DARK]);
	outers.forEach((o) => shapes.push([poly(clipDisk(o, cx, cy, R)), DARK]));
	return shapes;
};

// Cycling race: the three distinctive jerseys folded and stacked (green, polka dot, yellow)
const foldedJersey = (x, y, w, h, base, shade, extra = []) => {
	const cx = x + w / 2;
	return [[rr(x, y, w, h, 4), shade], [rr(x + 9, y, w - 18, h, 2), base], ...extra, [`M${cx - 9} ${y}L${cx} ${y + 8}L${cx + 9} ${y}Z`, shade], [`M${cx - 5} ${y}L${cx} ${y + 4.5}L${cx + 5} ${y}Z`, base === '#FFFFFF' ? '#E1E4EA' : base]];
};
const stackedJerseys = () => {
	const dots = [];
	for (let r = 0; r < 3; r++) for (let c = 0; c < 5; c++) dots.push([ell(32 + c * 13 + (r % 2) * 6.5, 72 + r * 8, 2.6), '#E04A3C']);
	return [...foldedJersey(13, 94, 96, 26, '#4CB050', '#3E9442'), [rr(13, 67, 96, 27, 4), '#C9D4EE'], ...foldedJersey(14, 68, 94, 25, '#FFFFFF', '#EEF1F7', dots), ...foldedJersey(15, 42, 92, 26, '#F4C44E', '#E0A82E')];
};

// Summer and winter games: medal, beanie. No rings, flame nor official name (protected trademarks).
const medal = (cx) => [
	[`M${cx - 17} 18H${cx - 3}L${cx + 5} 70H${cx - 5}Z`, '#6F8FD6'],
	[`M${cx + 17} 18H${cx + 3}L${cx - 5} 70H${cx + 5}Z`, '#E04A3C'],
	[ell(cx, 90, 28), '#E0A82E'],
	[ell(cx, 90, 22), '#F4C44E'],
	[ell(cx - 7, 84, 5, 7), '#FFE4A2'],
];
const beanie = () => {
	const shapes = [
		[ell(61, 56, 11), '#DCE5F7'],
		['M20 100C20 52 102 52 102 100Z', '#3F63B5'],
		['M23 84H99L100.5 92H21.5Z', '#6F8FD6'],
		[rr(16, 98, 90, 22, 5), '#6F8FD6'],
	];
	for (let x = 24; x <= 96; x += 9) shapes.push([rr(x, 101, 3, 16, 1.5), '#3F63B5']);
	return shapes;
};

/* ======================= Decors (behind the products) ======================= */

const confetti = (colors, n = 14) => Array.from({ length: n }, (_, i) => ({ d: rr(20 + i * 52 + (i % 2) * 14, -32 + (i % 3) * 6, 4, 7, 1), fill: colors[i % colors.length], anim: 'fall', i }));

const snow = () => [30, 95, 170, 225, 300, 365, 430, 500, 560, 615, 680, 735].map((x, i) => ({ d: ell(x, -30 + (i % 3) * 6, 2.6), fill: '#B0C2E8', anim: 'fall', i }));

const lightGarland = () => {
	const items = [];
	const colors = ['#E04A3C', '#F4C44E', '#6F8FD6', '#4CB050'];
	let d = 'M4 -26';
	for (let k = 0; k < 8; k++) d += `Q${4 + k * 94 + 47} -6 ${4 + (k + 1) * 94} -26`;
	items.push({ d, stroke: '#2E5E4E', sw: 2 });
	for (let k = 0; k < 8; k++) {
		const cx = 4 + k * 94 + 47;
		items.push({ d: rr(cx - 2, -18, 4, 4, 1), fill: '#2E5E4E' });
		items.push({ d: ell(cx, -9, 5, 6.5), fill: colors[k % 4], anim: 'twinkle', i: k });
	}
	return items;
};

// A string in two festoons, with items hanging from it and swaying
const string = (stroke) => ({ d: 'M4 -30Q190 -14 380 -30Q570 -14 756 -30', stroke, sw: 1.6 });
const yOnString = (x) => {
	const half = x < 380 ? (x - 4) / 376 : (x - 380) / 376;
	return -30 + 32 * half * (1 - half);
};

const bunting = () => {
	const colors = ['#FFE4A2', '#B0C2E8', '#EE9C94', '#ABE49A'];
	const items = [string('#95A7CC')];
	for (let k = 0; k < 16; k++) {
		const x = 18 + k * 46.5;
		const y = yOnString(x);
		items.push({ d: `M${x - 13} ${y.toFixed(1)}H${x + 13}L${x} ${(y + 22).toFixed(1)}Z`, fill: colors[k % 4], anim: 'sway', i: k });
	}
	return items;
};

const hangingPencils = () => {
	const colors = ['#E04A3C', '#F4C44E', '#4CB050', '#6F8FD6', '#EE9C94', '#F05E00'];
	const items = [string('#95A7CC')];
	for (let k = 0; k < 12; k++) {
		const x = 30 + k * 63;
		const y = yOnString(x);
		items.push({ d: `M${x - 4} ${y.toFixed(1)}H${x + 4}V${(y + 18).toFixed(1)}L${x} ${(y + 26).toFixed(1)}L${x - 4} ${(y + 18).toFixed(1)}Z`, fill: colors[k % 6], anim: 'sway', i: k });
	}
	return items;
};

const streamers = () => [70, 190, 320, 450, 580, 700].map((x, i) => ({ d: `M${x} -36q7 6 0 12q-7 6 0 12q7 6 0 12`, stroke: MULTI[i % MULTI.length], sw: 2.5, anim: 'sway', i }));

const floatingHearts = () =>
	[
		[150, 0.14],
		[240, 0.11],
		[395, 0.16],
		[470, 0.12],
		[545, 0.15],
		[615, 0.1],
		[745, 0.13],
	].map(([x, s], i) => ({ d: heart(x, 92, s), fill: i % 2 ? '#EE9C94' : '#E04A3C', anim: 'float', i }));

const bats = () =>
	[
		[150, -14],
		[300, -24],
		[470, -10],
		[620, -22],
		[720, -6],
	].map(([bx, by], i) => ({
		d: `M${bx - 14} ${by}Q${bx - 10} ${by - 6} ${bx - 6} ${by - 2}Q${bx - 3} ${by - 6} ${bx} ${by - 3}` + `Q${bx + 3} ${by - 6} ${bx + 6} ${by - 2}Q${bx + 10} ${by - 6} ${bx + 14} ${by}` + `Q${bx + 8} ${by + 1} ${bx + 5} ${by + 5}Q${bx + 2} ${by + 2} ${bx} ${by + 5}` + `Q${bx - 2} ${by + 2} ${bx - 5} ${by + 5}Q${bx - 8} ${by + 1} ${bx - 14} ${by}Z`,
		fill: '#585858',
		anim: 'bob',
		i,
	}));

const cobweb = () => [
	{ d: 'M2 -34L72 -34M2 -34L64 -12M2 -34L44 4M2 -34L6 8', stroke: '#C9D4EE', sw: 1.2 },
	{ d: 'M26 -34Q24 -26 22 -26Q14 -22 14 -17Q8 -12 3 -8', stroke: '#C9D4EE', sw: 1.2 },
	{ d: 'M50 -34Q46 -20 43 -18Q32 -10 29 -6Q20 0 5 0', stroke: '#C9D4EE', sw: 1.2 },
];

const fireworks = () =>
	[
		[180, -14, 16, '#E04A3C'],
		[420, -20, 18, '#F4C44E'],
		[650, -12, 15, '#6F8FD6'],
	].map(([cx, cy, r, c], i) => {
		let d = '';
		for (let k = 0; k < 8; k++) {
			const a = (k * Math.PI) / 4;
			d += `M${(cx + Math.cos(a) * r * 0.35).toFixed(1)} ${(cy + Math.sin(a) * r * 0.35).toFixed(1)}` + `L${(cx + Math.cos(a) * r).toFixed(1)} ${(cy + Math.sin(a) * r).toFixed(1)}`;
		}
		return { d, stroke: c, sw: 2.5, anim: 'burst', i };
	});

// A rider in yellow crossing the top of the shelf
const rider = () => {
	const bx = -30;
	return [
		{ d: `${ell(bx - 12, -10, 7)}${ell(bx + 12, -10, 7)}M${bx - 12} -10L${bx - 3} -22L${bx + 8} -22L${bx + 12} -10M${bx - 3} -22L${bx + 1} -10L${bx - 12} -10M${bx + 8} -22L${bx + 1} -10M${bx + 8} -22L${bx + 7} -26H${bx + 11}`, stroke: '#585858', sw: 1.6, anim: 'ride' },
		{ d: `M${bx - 3} -24L${bx + 5} -31L${bx + 10} -24`, stroke: '#F4C44E', sw: 3.5, anim: 'ride' },
		{ d: `M${bx - 3} -24L${bx + 1} -12`, stroke: '#3D2A1C', sw: 2, anim: 'ride' },
		{ d: ell(bx + 6, -35, 3.4), fill: '#3D2A1C', anim: 'ride' },
	];
};

const bouncingBalls = () =>
	[
		[260, -16],
		[520, -12],
	].map(([x, y], i) => ({ d: ell(x, y, 6), fill: '#3F63B5', anim: 'bob', i }));

const sparkles = (color) =>
	[
		[120, -20],
		[300, -8],
		[470, -24],
		[640, -10],
	].map(([x, y], i) => ({ d: `M${x} ${y - 7}L${x + 2} ${y - 2}L${x + 7} ${y}L${x + 2} ${y + 2}L${x} ${y + 7}L${x - 2} ${y + 2}L${x - 7} ${y}L${x - 2} ${y - 2}Z`, fill: color, anim: 'twinkle', i }));

/* ======================= Themes ======================= */

/** Seasonal looks by theme id: `replace` maps a shelf slot to its seasonal product, `decor` lists the decor items. */
export const SHELF_THEMES = {
	epiphanie: { replace: { 0: galette() }, decor: confetti(['#F4C44E', '#E9B44C']) },
	'saint-valentin': { replace: { 0: heartBox() }, decor: floatingHearts() },
	carnaval: { replace: { 0: carnivalMask() }, decor: [...streamers(), ...confetti(MULTI)] },
	paques: { replace: { 0: chocolateBunny(), 1: decoratedEgg() }, decor: bunting() },
	rentree: { replace: { 0: pencilBox(), 1: notebook() }, decor: hangingPencils() },
	halloween: { replace: { 0: pumpkinBucket() }, decor: [...cobweb(), ...bats()] },
	'saint-nicolas': { replace: { 0: treatBoot() }, decor: snow() },
	noel: { replace: { 0: giftBox() }, decor: [...snow(), ...lightGarland()] },
	'saint-sylvestre': { replace: { 0: partyHat() }, decor: [...fireworks(), ...confetti(MULTI, 10)] },
	cyclisme: { replace: { 0: stackedJerseys() }, decor: [...confetti(['#F4C44E', '#4CB050', '#E04A3C'], 10), ...rider()] },
	football: { replace: { 0: football() }, decor: [...confetti(MULTI, 10), ...bouncingBalls()] },
	'jeux-ete': { replace: { 0: medal(61) }, decor: [...confetti(['#F4C44E', '#C9D4EE', '#D99A3C']), ...sparkles('#F4C44E')] },
	'jeux-hiver': { replace: { 0: beanie(), 1: medal(174) }, decor: [...snow(), ...sparkles('#B0C2E8')] },
};
